// Admin (HQ) invoice list card.
//
// Feb-2026 (Sam): now carries an inline soft-delete button that
// hits ``DELETE /api/invoices/{id}`` (the canonical endpoint —
// preserves ``previous_status`` and stamps ``deleted_at`` so
// Restore works from ``/invoices/deleted``). Confirms with a
// shadcn AlertDialog per Sam's UX preference (no typed
// confirmation, no destructive-modal fatigue).
import { useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ArrowRight, Trash2, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StatusBadge, { deriveDisplayStatus } from "@/components/invoices/StatusBadge";
import api from "@/lib/api";
import { toast } from "sonner";

export const InvoiceCard = ({ invoice, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);

  const formattedDate = invoice.due_date
    ? format(parseISO(invoice.due_date), "MMM dd, yyyy")
    : "No date";

  const displayStatus = deriveDisplayStatus(invoice);
  const isOutstanding = displayStatus === "outstanding";
  const daysOverdue = invoice.days_overdue;
  const owed = typeof invoice.amount_owed === "number" ? invoice.amount_owed : invoice.total;
  // A partially reconciled overdue invoice must keep the "remaining
  // balance owed" visible even though the pill has flipped to
  // Outstanding — the distinction between "£500 fully outstanding"
  // and "£200 still outstanding on a £500 partial-paid invoice"
  // matters when Sandra chases payment.
  const wasPartial = isOutstanding && invoice.status === "partial";

  // Deleted invoices live in /invoices/deleted with their own Restore
  // affordance — hide the inline bin so we don't invite double-deletes.
  const canQuickDelete = invoice.status !== "deleted";

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await api.delete(`/invoices/${invoice.id}`);
      toast.success("Invoice moved to deleted");
      onDeleted?.(invoice.id);
    } catch {
      toast.error("Could not delete invoice");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card
      className="p-6 hover:shadow-md transition-all duration-200 border border-stone-200 hover:-translate-y-0.5 group rounded-2xl"
      data-testid={`invoice-card-${invoice.id}`}
    >
      <Link
        to={`/invoices/${invoice.id}`}
        className="block -m-6 p-6 rounded-2xl"
        data-testid={`invoice-card-link-${invoice.id}`}
      >
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center gap-8 min-w-0 flex-1">
            {/* Invoice Number + due date */}
            <div className="shrink-0 w-32">
              <p className="font-mono text-base font-semibold text-slate-900" data-testid="invoice-number">
                {invoice.invoice_number}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Due {formattedDate}
              </p>
              {isOutstanding && typeof daysOverdue === "number" && daysOverdue > 0 && (
                <p
                  className="text-xs font-semibold text-rose-600 mt-0.5"
                  data-testid={`invoice-days-overdue-${invoice.id}`}
                >
                  {daysOverdue} {daysOverdue === 1 ? "day" : "days"} overdue
                </p>
              )}
            </div>

            {/* Client */}
            <div className="hidden sm:block min-w-0 flex-1">
              <p className="font-semibold text-slate-900 truncate" data-testid="client-name">
                {invoice.client_name}
              </p>
              <p className="text-sm text-muted-foreground truncate">
                {invoice.client_email}
              </p>
            </div>
          </div>

          {/* Right-hand action area — status → amount → trash → arrow,
              all vertically centred so nothing overlaps the price
              (Feb-2026 Sam directive; replaces the earlier
              absolute-positioned trash icon). */}
          <div className="flex items-center gap-4 shrink-0">
            {/* Status — derived; "outstanding" visually replaces sent/partial. */}
            <StatusBadge status={displayStatus} />

            {/* Amount */}
            <div className="text-right w-28">
              <p className="font-mono text-lg font-bold text-slate-900" data-testid="invoice-total">
                £{invoice.total.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
              {wasPartial && typeof owed === "number" && owed > 0 && owed !== invoice.total && (
                <p
                  className="text-xs font-semibold text-rose-600 mt-0.5"
                  data-testid={`invoice-amount-owed-${invoice.id}`}
                >
                  £{owed.toLocaleString('en-GB', { minimumFractionDigits: 2 })} owed
                </p>
              )}
            </div>

            {/* Inline delete — sits between the amount and the arrow so it
                shares the same vertical rhythm as the other right-hand
                actions. Click is isolated with stopPropagation so the
                surrounding <Link> can still capture regular navigation. */}
            {canQuickDelete && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); }}
                    disabled={deleting}
                    data-testid={`invoice-card-delete-${invoice.id}`}
                    title="Delete invoice"
                    className="w-8 h-8 rounded-md border border-stone-200 hover:border-rose-300 hover:bg-rose-50 flex items-center justify-center transition-colors"
                  >
                    {deleting
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-500" />
                      : <Trash2 className="w-3.5 h-3.5 text-stone-500" />}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid={`invoice-card-delete-dialog-${invoice.id}`}>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Invoice?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Move invoice <span className="font-mono font-semibold">{invoice.invoice_number}</span> to the deleted bin? You can restore it later.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid={`invoice-card-delete-cancel-${invoice.id}`}>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleDelete}
                      data-testid={`invoice-card-delete-confirm-${invoice.id}`}
                      className="bg-destructive text-destructive-foreground"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {/* Arrow */}
            <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-stone-950 transition-colors" />
          </div>
        </div>
      </Link>
    </Card>
  );
};

export default InvoiceCard;
